import { ApplicationRef, Component, effect, inject } from '@angular/core';
import { RouterOutlet } from '@angular/router';
import { AlertModule } from '@full-fledged/alerts';
import { NavigationSpinnerComponent } from './shared/components/navigation-spinner/navigation-spinner.component';
import { OfflineNotificationComponent } from './shared/components/offline-notification/offline-notification.component';
import { ModalOverlayComponent } from './shared/components/modal-overlay/modal-overlay.component';
import { filter, interval, tap } from 'rxjs';
import { GlobalService } from './shared/services/global.service';
import { environment } from 'environments/environment';
import { SoftwareUpdateComponent } from './shared/components/software-update/software-update';
import { SatisfactionSurveyComponent } from './shared/components/satisfaction-survey/satisfaction-survey.component';

@Component({
    selector: 'app-root',
    templateUrl: './app.component.html',
    styleUrls: ['./app.component.scss'],
    imports: [
        AlertModule,
        ModalOverlayComponent,
        NavigationSpinnerComponent,
        OfflineNotificationComponent,
        RouterOutlet,
        SatisfactionSurveyComponent,
        SoftwareUpdateComponent,
    ],
})
export class AppComponent
{
    private readonly appRef = inject(ApplicationRef);
    private readonly globalService = inject(GlobalService);
    // private _formServiceProxy = inject(FormServiceProxy);
    // private _formVaultService = inject(FormVaultService);

    /**
     * Constructor
     */
    constructor() {
        this.appRef.isStable.pipe(filter((stable) => stable)).subscribe(() => {
            // console.log('App is stable now');
        });

        // The only thing this component does is render the iApply logo when you open the developer tools in your browser.
        console.log('%c iApply.com.au', "font-size:40px;position:relative;width:114px;height:47px;background: url('data:image/png;base64,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') no-repeat; background-size:114px 47px;color:transparent;");
        let fn = () => {
            // TODO! Implement this function server side to allow sync of any updated offline products.
            // this._formServiceProxy.checkProductVersions({})?.subscribe(async resp => {
            //     if (!resp.ok || !resp?.body) {
            //         return;
            //     }

            //     blobToText(resp.body).pipe(
            //         tap(x => {
            //             let respJs = JSON.parse(x);
            //             if (respJs == null || !respJs.Success || !respJs.result) {
            //                 return;
            //             }

            //             for (let property in respJs.result) {
            //                 this._formVaultService.downloadProductSearchResult(property).subscribe();
            //             }
            //         })
            //     ).subscribe();
            // });
        };

        effect(() => {
            if (this.globalService.isOnline()) {
                fn();
                interval((environment?.config?.callOfflineSyncProductsTimeoutMinutes || 5) * 60 * 1000)
                    .pipe(tap(x => {
                        if (this.globalService.isOnline()) {
                            fn();
                        }
                    }))
                    .subscribe();
            }
        });
    }
}
